import type { QueryClient } from '@tanstack/react-query'

// Utility functions for memory optimization

/**
 * Force garbage collection if available
 */
export const forceGarbageCollection = (): void => {
  if (typeof global !== 'undefined' && global.gc) {
    try {
      global.gc()
    } catch (error) {
      console.warn('Garbage collection failed:', error)
    }
  }
}

/**
 * Clean up event listeners and timers
 */
export const cleanupEventListeners = (
  element: EventTarget,
  listeners: Array<{ event: string; handler: EventListener; options?: boolean | AddEventListenerOptions }>
): void => {
  listeners.forEach(({ event, handler, options }) => {
    element.removeEventListener(event, handler, options)
  })
}

/**
 * Debounce function with proper cleanup
 */
export const createSafeDebounce = <T extends (...args: unknown[]) => void>(
  func: T,
  delay: number
): T & { cancel: () => void; flush: () => void } => {
  let timeoutId: NodeJS.Timeout | null = null
  let lastArgs: Parameters<T> | null = null

  const debounced = ((...args: Parameters<T>) => {
    lastArgs = args
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      func(...args)
      timeoutId = null
      lastArgs = null
    }, delay)
  }) as T & { cancel: () => void; flush: () => void }

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
      lastArgs = null
    }
  }

  debounced.flush = () => {
    if (timeoutId && lastArgs) {
      clearTimeout(timeoutId)
      func(...lastArgs)
      timeoutId = null
      lastArgs = null
    }
  }

  return debounced
}

/**
 * Memory monitoring and logging
 */
export const logMemoryUsage = (context: string): void => {
  // Only log in browser environment or Node.js with memory monitoring capabilities
  if (typeof window === 'undefined' && typeof process !== 'undefined' && process.memoryUsage) {
    const memory = process.memoryUsage()

    console.log(`[${context}] Memory Usage:`, {
      rss: `${Math.round(memory.rss / 1024 / 1024)}MB`,
      heapUsed: `${Math.round(memory.heapUsed / 1024 / 1024)}MB`,
      heapTotal: `${Math.round(memory.heapTotal / 1024 / 1024)}MB`,
      external: `${Math.round(memory.external / 1024 / 1024)}MB`
    })
  }
}

/**
 * React Query cache cleanup utility
 */
export const optimizeQueryCache = (queryClient: QueryClient): void => {
  if (typeof window === 'undefined') return

  try {
    void queryClient.invalidateQueries({ stale: true })

    // Force garbage collection after cache cleanup (only in browser with setTimeout)
    if (typeof setTimeout === 'function') {
      setTimeout(() => {
        forceGarbageCollection()
      }, 1000)
    }
  } catch (error) {
    console.warn('Query cache optimization failed:', error)
  }
}

/**
 * Periodic memory cleanup for production
 */
export const startMemoryCleanupInterval = (intervalMs: number = 5 * 60 * 1000): (() => void) => {
  // Only run in browser environment
  if (typeof window === 'undefined' || typeof setInterval === 'undefined') {
    return () => {} // Return no-op function
  }

  const interval = setInterval(() => {
    if (process.env.NODE_ENV === 'production') {
      logMemoryUsage('Periodic Cleanup')
      forceGarbageCollection()
    }
  }, intervalMs)

  return () => {
    if (typeof clearInterval !== 'undefined') {
      clearInterval(interval)
    }
  }
} 